/**
 * "How do you want to play?" (screens 01, 01b, 01c). Two bordered cards in a
 * radio group, each with an info button that opens a short explainer sheet,
 * and a gold strip restating the choice once a card is picked.
 */
import {
  Button,
  cn,
  Dialog,
  FOCUS_RING,
  REDUCED_MOTION_SAFE,
} from "@sportsmet/ui";
import { type KeyboardEvent, type ReactNode, useState } from "react";

import {
  SQUARES_GRID_TYPE_CHOOSE_OTHER,
  SQUARES_GRID_TYPE_CHOOSE_WHEN,
  SQUARES_GRID_TYPE_COPY,
  SQUARES_GRID_TYPE_INFO_CLOSE,
  SQUARES_GRID_TYPE_INFO_LABEL,
  SQUARES_SETUP_GRID_TYPE_HEADING,
} from "./squaresCopy";
import type { typeSquaresGridType } from "./squaresTypes";

const GRID_TYPES: readonly typeSquaresGridType[] = ["classic", "shared"];

export type typeSquaresGridTypeCardsProps = {
  value: typeSquaresGridType | null;
  onChange: (gridType: typeSquaresGridType) => void;
  /** The gold strip's text, or null to hide it. */
  confirmation: string | null;
};

const CheckIcon = (): ReactNode => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m4.5 10.5 3.5 3.5 7.5-8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** A tiny picture of each type: four small grids, or one big one. */
const GridTypePicture = ({
  gridType,
}: {
  gridType: typeSquaresGridType;
}): ReactNode =>
  gridType === "classic" ? (
    <div className="grid grid-cols-4 gap-2" aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          className="grid h-10 grid-cols-3 gap-1 rounded-sm border border-line p-1"
        >
          {[0, 1, 2, 3, 4, 5].map((cell) => (
            <span key={cell} className="rounded-sm bg-surface-sunken" />
          ))}
        </div>
      ))}
    </div>
  ) : (
    <div
      className="grid h-10 grid-cols-10 gap-1 rounded-sm border border-line p-1"
      aria-hidden="true"
    >
      {Array.from({ length: 20 }, (_, cell) => (
        <span key={cell} className="rounded-sm bg-surface-sunken" />
      ))}
    </div>
  );

function moveFocus(event: KeyboardEvent<HTMLDivElement>): void {
  const keys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"];
  if (!keys.includes(event.key)) return;
  const radios = [
    ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
      '[role="radio"]',
    ),
  ];
  const current = radios.indexOf(document.activeElement as HTMLButtonElement);
  const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
  const next = forward
    ? (current + 1) % radios.length
    : (current - 1 + radios.length) % radios.length;
  event.preventDefault();
  radios[next]?.focus();
  radios[next]?.click();
}

export const SquaresGridTypeCards = (
  props: typeSquaresGridTypeCardsProps,
): ReactNode => {
  const [infoType, setInfoType] = useState<typeSquaresGridType | null>(null);
  const infoCopy = infoType === null ? null : SQUARES_GRID_TYPE_COPY[infoType];

  return (
    <section className="flex flex-col gap-3" data-testid="squares-grid-types">
      <h3 id="squares-grid-type-heading" className="text-body-strong text-ink">
        {SQUARES_SETUP_GRID_TYPE_HEADING}
      </h3>
      <div
        role="radiogroup"
        aria-labelledby="squares-grid-type-heading"
        className="flex flex-col gap-3"
        onKeyDown={moveFocus}
      >
        {GRID_TYPES.map((gridType, index) => {
          const copy = SQUARES_GRID_TYPE_COPY[gridType];
          const isSelected = props.value === gridType;
          const isTabStop = props.value === null ? index === 0 : isSelected;
          return (
            <div
              key={gridType}
              className={cn(
                "relative flex w-full items-start gap-3 rounded-md border-2 bg-surface p-4",
                "transition-colors duration-200 ease-standard",
                REDUCED_MOTION_SAFE,
                isSelected ? "border-primary" : "border-hairline",
              )}
            >
              <button
                type="button"
                role="radio"
                aria-checked={isSelected}
                tabIndex={isTabStop ? 0 : -1}
                data-testid={`squares-grid-type-${gridType}`}
                className={cn(
                  "flex min-h-touch-target flex-1 flex-col gap-2 text-left",
                  FOCUS_RING,
                )}
                onClick={() => {
                  props.onChange(gridType);
                }}
              >
                <span className="flex items-center gap-2 text-body-strong text-ink">
                  {copy.title}
                  {isSelected ? (
                    <span className="text-primary">
                      <CheckIcon />
                    </span>
                  ) : null}
                </span>
                <span className="text-caption text-ink-secondary">
                  {copy.body}
                </span>
                <GridTypePicture gridType={gridType} />
              </button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={SQUARES_GRID_TYPE_INFO_LABEL(copy.title)}
                data-testid={`squares-grid-type-info-${gridType}`}
                onClick={() => {
                  setInfoType(gridType);
                }}
              >
                <span
                  aria-hidden="true"
                  className="flex h-icon-md w-icon-md items-center justify-center rounded-full border border-current text-caption"
                >
                  i
                </span>
              </Button>
            </div>
          );
        })}
      </div>
      {props.confirmation === null ? null : (
        <p
          role="status"
          data-testid="squares-grid-type-strip"
          className="animate-fade-in rounded-sm border border-warning bg-warning-tint px-3 py-2 text-caption text-ink motion-reduce:animate-none"
        >
          {props.confirmation}
        </p>
      )}
      <Dialog
        open={infoCopy !== null}
        onOpenChange={(open) => {
          if (!open) setInfoType(null);
        }}
        title={infoCopy?.title ?? ""}
        autoFocus={false}
        footer={
          <Button
            fullWidth
            onClick={() => {
              setInfoType(null);
            }}
          >
            {SQUARES_GRID_TYPE_INFO_CLOSE}
          </Button>
        }
      >
        {infoType === null || infoCopy === null ? null : (
          <div
            className="flex flex-col gap-4"
            data-testid="squares-grid-type-info"
          >
            <GridTypePicture gridType={infoType} />
            <ol className="flex flex-col gap-2">
              {infoCopy.points.map((point, index) => (
                <li key={point} className="flex gap-3 text-body text-ink">
                  <span
                    aria-hidden="true"
                    className="flex h-icon-lg w-icon-lg shrink-0 items-center justify-center rounded-full bg-primary-tint text-caption text-primary"
                  >
                    {index + 1}
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ol>
            <div className="flex flex-col gap-1">
              <p className="text-body-strong text-ink">
                {SQUARES_GRID_TYPE_CHOOSE_WHEN}
              </p>
              <p className="text-body text-ink-secondary">
                {infoCopy.chooseWhen}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-body-strong text-ink">
                {SQUARES_GRID_TYPE_CHOOSE_OTHER}
              </p>
              <p className="text-body text-ink-secondary">
                {infoCopy.chooseOtherWhen}
              </p>
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
};
